import { useCallback, useEffect, useMemo, useState } from 'react'

import { BalancesAndAllowances } from '@cowprotocol/balances-and-allowances'
import { ZERO_FRACTION } from '@cowprotocol/common-const'
import { useTimeAgo } from '@cowprotocol/common-hooks'
import { formatDateWithTimezone, getAddress, getIsNativeToken } from '@cowprotocol/common-utils'
import { SupportedChainId } from '@cowprotocol/cow-sdk'
import { Currency, Price } from '@cowprotocol/currency'
import { Command } from '@cowprotocol/types'
import { PercentDisplay, percentIsAlmostHundred, TokenAmount } from '@cowprotocol/ui'
import { useIsSafeWallet } from '@cowprotocol/wallet'

import { OrderStatus } from 'legacy/state/orders/actions'
import { getEstimatedExecutionPrice } from 'legacy/state/orders/utils'

import { PendingOrderPrices } from 'modules/orders'
import { useIsFallbackHandlerRequired } from 'modules/twap'

import { useIsProviderNetworkDeprecated } from 'common/hooks/useIsProviderNetworkDeprecated'
import { useSafeMemo } from 'common/hooks/useSafeMemo'
import { CurrencyLogoPair } from 'common/pure/CurrencyLogoPair'
import { RateInfo } from 'common/pure/RateInfo'
import { getQuoteCurrency } from 'common/services/getQuoteCurrency'
import { isOrderCancellable } from 'common/utils/isOrderCancellable'
import { getSellAmountWithFee } from 'utils/orderUtils/getSellAmountWithFee'
import { ParsedOrder } from 'utils/orderUtils/parseOrder'

import * as styledEl from './OrderRow.styled'
import { TableRow } from './OrderRow.styled'

import { usePricesDifference } from '../../hooks/usePricesDifference'
import { OrderContextMenu } from '../../pure/ContextMenu/OrderContextMenu.pure'
import { CurrencyAmountItem } from '../../pure/CurrencyAmountItem/CurrencyAmountItem.pure'
import { WarningReason } from '../../pure/OrderEstimatedExecutionPrice/orderEstimatedExecutionPrice.constants'
import { OrderFillsAt } from '../../pure/OrderFillsAt/OrderFillsAt.pure'
import { OrderFillsAtWithDistance } from '../../pure/OrderFillsAtWithDistance/OrderFillsAtWithDistance.pure'
import { OrderMarketPrice } from '../../pure/OrderMarketPrice/OrderMarketPrice.pure'
import {
  CheckboxCheckmark,
  TableRowCheckbox,
  TableRowCheckboxWrapper,
} from '../../pure/OrdersTable/Row/Checkbox/Checkbox.styled'
import { OrderRowWarningEstimatedPrice } from '../../pure/OrdersTable/Row/WarningEstimatedPrice/OrderRowWarningEstimatedPrice.pure'
import {
  FallbackHandlerWarningTooltip,
  WarningTooltip,
} from '../../pure/OrdersTable/Row/WarningTooltip/WarningTooltip.pure'
import { OrderStatusBox } from '../../pure/OrderStatusBox/OrderStatusBox.pure'
import { OrderActions } from '../../state/ordersTable.types'
import { getIsFallbackHandlerUnfillable } from '../../utils/getIsFallbackHandlerUnfillable'
import { OrderParams } from '../../utils/getOrderParams'
import { shouldShowDashForExpiration } from '../../utils/shouldShowDashForExpiration'
import { getActivityUrl } from '../../utils/url/getActivityUrl'

// Constants
const TIME_AGO_UPDATE_INTERVAL = 3000

export interface OrderRowProps {
  order: ParsedOrder
  prices: PendingOrderPrices | undefined | null
  spotPrice: Price<Currency, Currency> | undefined | null
  isRateInverted: boolean
  isHistoryTab: boolean
  isRowSelectable: boolean
  isRowSelected: boolean
  isChild?: boolean
  isExpanded?: boolean
  orderParams: OrderParams
  onClick: Command
  orderActions: OrderActions
  children?: React.ReactNode
  childOrders?: ParsedOrder[]
  isTwapTable?: boolean
  chainId: SupportedChainId
  balancesAndAllowances: BalancesAndAllowances
}

// TODO: Break down this large function into smaller functions
// TODO: Add proper return type annotation
// TODO: Reduce function complexity by extracting logic
// eslint-disable-next-line max-lines-per-function, @typescript-eslint/explicit-function-return-type, complexity
export function OrderRow({
  order,
  isRateInverted: isGloballyInverted,
  isHistoryTab,
  isRowSelectable,
  isRowSelected,
  isChild,
  isExpanded,
  orderActions,
  orderParams,
  onClick,
  prices,
  spotPrice,
  children,
  childOrders,
  isTwapTable,
  chainId,
  balancesAndAllowances,
}: OrderRowProps) {
  const { buyAmount, rateInfoParams, hasEnoughAllowance, hasEnoughBalance } = orderParams
  const { creationTime, expirationTime, status } = order
  const { filledPercentDisplay, executedPrice } = order.executionData
  const { inputCurrencyAmount, outputCurrencyAmount } = rateInfoParams
  const { feeAmount } = prices || {}
  const estimatedExecutionPrice = useSafeMemo(() => {
    return spotPrice && feeAmount && getEstimatedExecutionPrice(order, spotPrice, feeAmount.quotient.toString())
  }, [spotPrice, feeAmount, order])
  const isSafeWallet = useIsSafeWallet()

  const isChainIdDeprecated = useIsProviderNetworkDeprecated()
  const showCancellationModal = useMemo(() => {
    return orderActions.getShowCancellationModal(order)
  }, [orderActions, order])
  const alternativeOrderModalContext = useMemo(
    () => (isChainIdDeprecated ? undefined : orderActions.getAlternativeOrderModalContext(order)),
    [order, orderActions, isChainIdDeprecated],
  )

  const withAllowanceWarning = hasEnoughAllowance === false
  const withWarning =
    (hasEnoughBalance === false || withAllowanceWarning) &&
    // show the warning only for pending and scheduled orders, but not for presignature pending
    status !== OrderStatus.PRESIGNATURE_PENDING &&
    (status === OrderStatus.PENDING || status === OrderStatus.SCHEDULED)
  const isOrderScheduled = order.status === OrderStatus.SCHEDULED

  // eslint-disable-next-line react-hooks/purity
  const isScheduledCreating = isOrderScheduled && Date.now() > creationTime.getTime()
  const expirationTimeAgo = useTimeAgo(expirationTime, TIME_AGO_UPDATE_INTERVAL)
  const creationTimeAgo = useTimeAgo(creationTime, TIME_AGO_UPDATE_INTERVAL)
  const fulfillmentTimeAgo = useTimeAgo(
    order.fulfillmentTime ? new Date(order.fulfillmentTime) : undefined,
    TIME_AGO_UPDATE_INTERVAL,
  )
  const activityUrl = chainId ? getActivityUrl(chainId, order) : undefined

  const isInvertedState = useState(() => {
    // On mount, apply smart quote selection
    const quoteCurrency = getQuoteCurrency(chainId, inputCurrencyAmount, outputCurrencyAmount)
    return getAddress(quoteCurrency) === getAddress(inputCurrencyAmount?.currency)
  })
  const [isInverted, setIsInverted] = isInvertedState
  const toggleIsInverted = useCallback(() => setIsInverted((curr) => !curr), [setIsInverted])

  // Toggle isInverted whenever isGloballyInverted changes
  useEffect(() => {
    toggleIsInverted()
  }, [isGloballyInverted, toggleIsInverted])

  const executedPriceInverted = isInverted ? executedPrice?.invert() : executedPrice

  const priceDiffs = usePricesDifference(estimatedExecutionPrice, spotPrice, isInverted)

  const isExecutedPriceZero = executedPriceInverted !== undefined && executedPriceInverted?.equalTo(ZERO_FRACTION)

  // The Safe ComposableCoW fallback handler being reset/removed is a per-account condition, so it is
  // resolved here in the view (not persisted onto the order) and combined with the per-order status.
  // It only applies to composable (TWAP) orders; surface it with the same danger design as the
  // balance/allowance warnings (see issue #5426).
  const isFallbackHandlerRequired = useIsFallbackHandlerRequired()
  const isFallbackHandlerUnfillable =
    isTwapTable === true && getIsFallbackHandlerUnfillable(status, isFallbackHandlerRequired)

  const isUnfillable =
    isFallbackHandlerUnfillable ||
    (!percentIsAlmostHundred(filledPercentDisplay) && (isExecutedPriceZero || withWarning))

  const inputTokenSymbol = order.inputToken.symbol || ''

  const warningReason = isFallbackHandlerUnfillable
    ? WarningReason.FallbackHandler
    : hasEnoughBalance === false
      ? WarningReason.Balance
      : hasEnoughAllowance === false
        ? WarningReason.Allowance
        : undefined

  const onApprove = withAllowanceWarning ? () => orderActions.approveOrderToken(order.inputToken) : undefined

  const estimatedPriceWarning = withWarning ? (
    <OrderRowWarningEstimatedPrice
      approveOrderToken={orderActions.approveOrderToken}
      balancesAndAllowances={balancesAndAllowances}
      chainId={chainId}
      childOrders={childOrders}
      isChild={isChild}
      isInverted={isInverted}
      isTwapTable={isTwapTable}
      order={order}
      withAllowanceWarning={withAllowanceWarning}
    />
  ) : undefined

  const orderFillsAt = (
    <OrderFillsAt
      childOrders={childOrders}
      estimatedExecutionPrice={estimatedExecutionPrice}
      estimatedPriceWarning={estimatedPriceWarning}
      isChild={isChild}
      isFallbackHandlerRequired={isFallbackHandlerRequired}
      isInverted={isInverted}
      isSafeWallet={isSafeWallet}
      isTwapTable={isTwapTable}
      isUnfillable={isUnfillable}
      onApprove={onApprove}
      order={order}
      priceDiffs={priceDiffs}
      prices={prices}
      rateInfoParams={rateInfoParams}
      spotPrice={spotPrice}
      warningReason={warningReason}
      withWarning={withWarning}
    />
  )

  return (
    <TableRow
      data-id={order.id}
      isChildOrder={isChild}
      isHistoryTab={isHistoryTab}
      isRowSelectable={isRowSelectable}
      isTwapTable={isTwapTable}
      isExpanded={isExpanded}
    >
      {/*Checkbox for multiple cancellation*/}
      {isRowSelectable && !isHistoryTab && (
        <TableRowCheckboxWrapper>
          <TableRowCheckbox
            type="checkbox"
            checked={isRowSelected}
            disabled={getIsNativeToken(order.inputToken) || !isOrderCancellable(order)}
            onChange={() => orderActions.toggleOrderForCancellation(order)}
          />
          <CheckboxCheckmark />
        </TableRowCheckboxWrapper>
      )}

      {/* Order sell/buy tokens */}
      <styledEl.CurrencyCell>
        <CurrencyLogoPair sellToken={order.inputToken} buyToken={buyAmount.currency} clickable onClick={onClick} />
        <styledEl.CurrencyAmountWrapper clickable onClick={onClick}>
          <CurrencyAmountItem amount={getSellAmountWithFee(order)} />
          <CurrencyAmountItem amount={buyAmount} />
        </styledEl.CurrencyAmountWrapper>
      </styledEl.CurrencyCell>

      {/* Limit price */}
      <styledEl.PriceElement>
        <RateInfo
          prependSymbol={false}
          isInvertedState={isInvertedState}
          noLabel={true}
          doNotUseSmartQuote
          isInverted={isInverted}
          rateInfoParams={rateInfoParams}
          opacitySymbol={true}
        />
      </styledEl.PriceElement>

      {/* Non-history tab columns */}
      {!isHistoryTab ? (
        <>
          <styledEl.PriceElement onClick={toggleIsInverted}>
            {/* Special case for PRESIGNATURE_PENDING - return just the signing content */}
            {order.status === OrderStatus.PRESIGNATURE_PENDING ? (
              orderFillsAt
            ) : (
              <OrderFillsAtWithDistance
                order={order}
                withWarning={withWarning}
                warningReason={warningReason}
                onApprove={onApprove}
                isInverted={isInverted}
                isUnfillable={isUnfillable}
                isFallbackHandlerRequired={isFallbackHandlerRequired}
                estimatedExecutionPrice={estimatedExecutionPrice}
                spotPrice={spotPrice}
                estimatedPriceWarning={estimatedPriceWarning}
                priceDiffs={priceDiffs}
                childOrders={childOrders}
                isTwapTable={isTwapTable}
                isChild={isChild}
                orderFillsAt={orderFillsAt}
              />
            )}
          </styledEl.PriceElement>
          <styledEl.PriceElement onClick={toggleIsInverted}>
            <OrderMarketPrice
              order={order}
              withWarning={withWarning}
              childOrders={childOrders}
              isTwapTable={isTwapTable}
              spotPrice={spotPrice}
              isChild={isChild}
              isInverted={isInverted}
            />
          </styledEl.PriceElement>

          {/* Expires and Created for open orders */}
          <styledEl.CellElement doubleRow>
            <b
              title={
                expirationTime && !shouldShowDashForExpiration(order)
                  ? formatDateWithTimezone(expirationTime)
                  : undefined
              }
            >
              {shouldShowDashForExpiration(order) ? '-' : expirationTimeAgo}
            </b>
            <i title={creationTime && !isScheduledCreating ? formatDateWithTimezone(creationTime) : undefined}>
              {isScheduledCreating ? 'Creating...' : creationTimeAgo}
            </i>
          </styledEl.CellElement>
        </>
      ) : (
        <>
          {/* History tab columns */}

          {/* Execution price */}
          <styledEl.PriceElement onClick={toggleIsInverted}>
            {executedPriceInverted ? (
              <TokenAmount
                amount={executedPriceInverted}
                tokenSymbol={executedPriceInverted?.quoteCurrency}
                opacitySymbol
                clickable
                noTitle
              />
            ) : (
              '-'
            )}
          </styledEl.PriceElement>

          <styledEl.CellElement>
            {order.status === OrderStatus.FULFILLED && fulfillmentTimeAgo ? (
              <span title={order.fulfillmentTime ? formatDateWithTimezone(new Date(order.fulfillmentTime)) : undefined}>
                {fulfillmentTimeAgo}
              </span>
            ) : (
              '-'
            )}
          </styledEl.CellElement>

          <styledEl.CellElement>
            <span title={creationTime ? formatDateWithTimezone(creationTime) : undefined}>{creationTimeAgo}</span>
          </styledEl.CellElement>
        </>
      )}

      {/* Filled % */}
      <styledEl.CellElement doubleRow clickable onClick={onClick}>
        <styledEl.FilledPercentageContainer>
          <styledEl.ProgressBar value={filledPercentDisplay}></styledEl.ProgressBar>
          <b>
            <PercentDisplay percent={filledPercentDisplay} />
          </b>
        </styledEl.FilledPercentageContainer>
      </styledEl.CellElement>

      {/* Status label */}
      {!children && (
        <styledEl.CellElement>
          <styledEl.StatusBox>
            <OrderStatusBox
              order={order}
              withWarning={withWarning || isFallbackHandlerUnfillable}
              onClick={onClick}
              WarningTooltip={
                isFallbackHandlerUnfillable ? (
                  <FallbackHandlerWarningTooltip />
                ) : (
                  <WarningTooltip
                    hasEnoughBalance={hasEnoughBalance ?? false}
                    hasEnoughAllowance={hasEnoughAllowance ?? false}
                    inputTokenSymbol={inputTokenSymbol}
                    isOrderScheduled={isOrderScheduled}
                    onApprove={() => orderActions.approveOrderToken(order.inputToken)}
                  />
                )
              }
            />
          </styledEl.StatusBox>
        </styledEl.CellElement>
      )}

      {/* Children (e.g. ToggleExpandButton for parent orders) */}
      {children}

      {/* Add empty cell for child TWAP orders */}
      {isTwapTable && isChild && <styledEl.CellElement />}

      {/* Add empty cell for signing orders - only for TWAP */}
      {isTwapTable && order.status === OrderStatus.PRESIGNATURE_PENDING && <styledEl.CellElement />}

      {/* Action content menu */}
      <styledEl.CellElement>
        <OrderContextMenu
          activityUrl={activityUrl}
          openReceipt={onClick}
          showCancellationModal={showCancellationModal}
          alternativeOrderModalContext={alternativeOrderModalContext}
        />
      </styledEl.CellElement>
    </TableRow>
  )
}
